import differenceInCalendarDays from 'date-fns/differenceInCalendarDays'
import endOfDay from 'date-fns/endOfDay'
import format from 'date-fns/format'
import formatISO from 'date-fns/formatISO'
import getDate from 'date-fns/getDate'
import getHours from 'date-fns/getHours'
import getMinutes from 'date-fns/getMinutes'
import getMonth from 'date-fns/getMonth'
import getYear from 'date-fns/getYear'
import intlFormat from 'date-fns/intlFormat'
import isSameDay from 'date-fns/isSameDay'
import parseISO from 'date-fns/parseISO'

/**
 * return thai date in this format 9 มิถุนายน 2566
 * @param date
 * @returns string
 */
export const formatThaiDate = (date: string | Date | null) => {
  if (!date) return ''

  let givenDate = date
  if (typeof givenDate === 'string') {
    givenDate = parseISO(givenDate)
  }
  return intlFormat(
    givenDate,
    { day: 'numeric', month: 'long', year: 'numeric' },
    { locale: 'th-TH' }
  )
}

/**
 * Format date yyyy-mm-dd
 */
export const formatDate = (date: Date) => {
  const d = new Date(date)
  let month = `${d.getMonth() + 1}`
  let day = `${d.getDate()}`
  const year = d.getFullYear()

  if (month.length < 2) month = `0${month}`
  if (day.length < 2) day = `0${day}`

  return [year, month, day].join('-')
}

/**
 * Format date dd/MM/yyyy HH:mm
 */
export const formatDateTime = (date: Date | string) => {
  if (!date) return ''

  let givenDate = date
  if (typeof givenDate === 'string') {
    givenDate = parseISO(givenDate)
  }

  return format(givenDate, 'dd/MM/yyyy HH:mm')
}

/**
 * Format date dd/mm/yyyy
 */

export const formatThaiDateNumeric = (
  date: Date | string | null,
  options?: { isShort?: boolean }
): string => {
  if (!date) return ''

  let givenDate = date
  if (typeof givenDate === 'string') {
    givenDate = parseISO(givenDate)
  }

  const d = new Date(givenDate)
  let month = `${d.getMonth() + 1}`
  let day = `${d.getDate()}`
  let year = `${d.getFullYear()}`

  if (month.length < 2) month = `0${month}`
  if (day.length < 2) day = `0${day}`

  if (options?.isShort) {
    year = year.slice(-2)
  }

  return [day, month, year].join('/')
}

/**
 * Format date as dd/MM/yyyy with Buddhist Era year (CE + 543)
 */
export const formatThaiDateWithBuddhistYear = (date: Date | string | null) => {
  if (!date) return ''

  let givenDate = date
  if (typeof givenDate === 'string') {
    givenDate = parseISO(givenDate)
  }

  const d = new Date(givenDate)
  let month = `${d.getMonth() + 1}`
  let day = `${d.getDate()}`
  const yearCE = d.getFullYear()
  const yearBE = yearCE + 543 // Convert to Buddhist Era

  if (month.length < 2) month = `0${month}`
  if (day.length < 2) day = `0${day}`

  return [day, month, yearBE].join('/')
}

export const getDateFromInputDateTime = (date: Date, time: Date) => {
  const day = getDate(date)
  const month = getMonth(date)
  const year = getYear(date)
  const hr = getHours(time)
  const minute = getMinutes(time)

  const dateTime = new Date(year, month, day, hr, minute)
  return dateTime
}

export const getMaxTimeFromInputDate = (date: Date) => {
  const now = new Date()
  const dateTime = getDateFromInputDateTime(date, now)
  if (isSameDay(dateTime, now)) {
    return now
  }
  return endOfDay(dateTime)
}

export const formatTime = (date: Date | string) => {
  if (!date) return ''

  let givenDate = date
  if (typeof givenDate === 'string') {
    givenDate = parseISO(givenDate)
  }
  return format(givenDate, 'HH:mm')
}

export const strToISOStr = (date: string) => {
  if (!date) return date

  const dateCls = new Date(date)

  const dateISO = parseISO(dateCls.toISOString())
  return formatISO(dateISO, { representation: 'complete' }).substring(0, 19) + 'Z'
}

export const formatThaiDateTime = (date: string | Date | null) => {
  if (!date) return ''

  let givenDate = date
  if (typeof givenDate === 'string') {
    givenDate = parseISO(givenDate)
  }
  const dateStr = intlFormat(
    givenDate,
    { day: 'numeric', month: 'long', year: 'numeric' },
    { locale: 'th-TH' }
  )
  const hour = givenDate.getHours().toString().padStart(2, '0')
  const minute = givenDate.getMinutes().toString().padStart(2, '0')
  return `${dateStr} ${hour}:${minute}`
}

/**
 * Format date to ISO string with timezone offset (e.g., 2025-10-07T18:27:15-10:00)
 */
export const formatISOWithTimezone = (date: Date | string) => {
  if (!date) return ''

  let givenDate = date
  if (typeof givenDate === 'string') {
    givenDate = parseISO(givenDate)
  }

  return formatISO(givenDate, { representation: 'complete' })
}

/**
 * Format date as D MMM YY (e.g., 9 มิ.ย. 66)
 */

export const formatThaiShortDateTwoDigitYear = (
  date: Date | string | null,
  options?: { showTime?: boolean }
) => {
  if (!date) return ''

  let givenDate = date
  if (typeof givenDate === 'string') {
    givenDate = parseISO(givenDate)
  }

  const d = new Date(givenDate)

  const day = d.getDate()

  const monthNames = [
    'ม.ค.',
    'ก.พ.',
    'มี.ค.',
    'เม.ย.',
    'พ.ค.',
    'มิ.ย.',
    'ก.ค.',
    'ส.ค.',
    'ก.ย.',
    'ต.ค.',
    'พ.ย.',
    'ธ.ค.',
  ]

  const month = monthNames[d.getMonth()]

  const yearBE = d.getFullYear() + 543
  const year2 = yearBE.toString().slice(-2)

  const dateText = `${day} ${month} ${year2}`

  if (!options?.showTime) return dateText

  const hours = String(d.getHours()).padStart(2, '0')
  const minutes = String(d.getMinutes()).padStart(2, '0')

  return `${dateText} ${hours}:${minutes}`
}

export const getTodayDate = (): Date => {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  return today
}

export const formatUTCDateTime = (date: Date | string) => {
  if (!date) return ''

  let givenDate = date
  if (typeof givenDate === 'string') {
    givenDate = parseISO(givenDate)
  }

  return new Date(givenDate).toISOString()
}

/**
 * Age in days on `onDate` for a cycle that started on `startDate`, counting
 * the start day as day 1 (local calendar days). Null when either date is
 * missing/invalid or `onDate` is before the start.
 *
 * e.g. start 15 Jun, on 19 Sep → 97.
 */
export const getAgeOnDate = (
  startDate: string | Date | null | undefined,
  onDate: string | Date | null | undefined
): number | null => {
  if (!startDate || !onDate) return null
  const start = new Date(startDate)
  const on = new Date(onDate)
  if (isNaN(start.getTime()) || isNaN(on.getTime())) return null
  const age = differenceInCalendarDays(on, start) + 1
  return age >= 1 ? age : null
}
